import { useState, useCallback, useRef, useLayoutEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Button, Drawer, Skeleton, Tooltip } from "antd";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useLocation, useParams } from "react-router";
import { chatService } from "../../services/chatService";
import { useUser } from "../../contexts/UserContext";
import { getFirstLetter } from "../../utils/UserInfoFormatting";
import { getScreenContext, getPageContent } from "../../utils/screenContext";
import { getTrialIdFromPathname } from "../../utils/scopeRouting";
import { stripContextPrefix } from "../../components/AskVivo/askVivo.utils";
import { buildPlanContext } from "../../utils/buildPlanContext";
import { useTrialPlans } from "../../hooks/useTrialPlans";
import ChatInput from "./ChatInput";
import VivoIcon from "../../assets/icons/vivo_logo_white.svg?react";
import VivoLoadIcon from "../../assets/icons/vivoLoading.gif";
import MarkdownRenderer from "../UI/MarkdownRenderer";

const DATA_SOURCES = ["vivo_data_views", "protocol", "plot"];

// Tabs/routes where the FAB is hidden (they have their own AskVivo experience)
const HIDDEN_TABS = ["askvivo"];

interface ChatEntry {
  // Client-side identity: `interaction_id` only arrives with the answer, so it
  // cannot key an entry that is still in flight or that failed.
  entryId: string;
  interaction_id: string;
  session_id: string;
  input: string;
  output: string;
  selected_sources: string;
  timestamp: string;
  requested_clarification: boolean;
  isLoading: boolean;
  hasError: boolean;
}

interface DrawerChatItemProps {
  chat: ChatEntry | null | undefined;
  userFirstInitial: string;
}

function DrawerChatItem({ chat, userFirstInitial }: DrawerChatItemProps) {
  if (!chat) return null;

  return (
    <div className="pt-3">
      <div className="flex gap-3 mb-3">
        <Button
          shape="circle"
          size="small"
          className="initials-button flex-shrink-0"
          style={{ minWidth: 28, width: 28, height: 28, fontSize: 12 }}
        >
          {userFirstInitial || "?"}
        </Button>
        <div className="prompt-box text-sm">
          {stripContextPrefix(chat.input || "")}
        </div>
      </div>
      <div className="flex gap-3 mt-3 mb-4">
        <Button
          shape="circle"
          size="small"
          icon={
            chat.isLoading ? (
              <img
                src={VivoLoadIcon}
                alt="Loading..."
                style={{ width: 16, height: 16 }}
              />
            ) : (
              <VivoIcon style={{ width: 14, height: 14 }} className="mb-px" />
            )
          }
          className="ergo-button flex-shrink-0"
          style={{ minWidth: 28, width: 28, height: 28 }}
        />
        {chat.isLoading ? (
          <Skeleton active paragraph={{ rows: 3 }} className="flex-1" />
        ) : (
          <div className="flex-1 min-w-0">
            {chat.output ? (
              <div className="answer-box text-sm markdown">
                <MarkdownRenderer content={chat.output} />
              </div>
            ) : (
              <div className="answer-box text-sm markdown text-gray-400">
                {chat.hasError
                  ? "There was an error. Please try again."
                  : "No response."}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AskVivoDrawer() {
  const location = useLocation();
  // Supports both trial context (:trialId) and planning context (:id + :planId)
  const { trialId, id, planId } = useParams<{
    trialId?: string;
    id?: string;
    planId?: string;
  }>();
  // The drawer renders from a layout route, where a child route's `:trialId` is
  // not reliably in params. The pathname always carries it, so that is the
  // primary source; params remain the fallback for planning routes.
  const effectiveTrialId =
    getTrialIdFromPathname(location.pathname) || trialId || id;
  const { userInfo } = useUser();
  const userFirstInitial = getFirstLetter(userInfo?.first_name ?? "");
  const { getPlan } = useTrialPlans();

  const [open, setOpen] = useState(false);
  const [chatHistory, setChatHistory] = useState<ChatEntry[]>([]);
  const [sessionId, setSessionId] = useState<string>(() => uuidv4());
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [isNewSession, setIsNewSession] = useState(true);

  const bottomRef = useRef<HTMLDivElement>(null);

  const activeTab = location.pathname.split("/").at(-1) ?? "";
  const isOnHiddenPage = HIDDEN_TABS.includes(activeTab);

  const planningParams = id && planId ? { id, planId } : undefined;
  const screenContext = getScreenContext(
    location.pathname,
    effectiveTrialId,
    planningParams,
  );

  // For planning routes, enrich context with current plan data and per-site DQS/profile details.
  const currentPlan = planId ? getPlan(planId) : undefined;
  const planContext = currentPlan
    ? buildPlanContext(currentPlan, activeTab)
    : "";

  useLayoutEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [chatHistory]);

  const handleNewChat = () => {
    setChatHistory([]);
    setSessionId(uuidv4());
    setIsNewSession(true);
  };

  const handleSearch = useCallback(
    (prompt: string) => {
      if (prompt?.trim() === "") return;

      setIsNewSession(false);
      setIsChatLoading(true);

      const localChatHistory: ChatEntry[] = [
        ...chatHistory,
        {
          entryId: uuidv4(),
          interaction_id: "",
          session_id: "",
          input: prompt,
          output: "",
          selected_sources: "",
          timestamp: "",
          requested_clarification: false,
          isLoading: true,
          hasError: false,
        },
      ];
      setChatHistory(localChatHistory);

      const currentSessionId = sessionId || uuidv4();
      if (!sessionId) setSessionId(currentSessionId);

      const pageContent = getPageContent();
      const contextParts = [
        screenContext.contextPrefix,
        planContext,
        pageContent
          ? `[Page content visible to the user:\n${pageContent}]`
          : "",
      ]
        .filter(Boolean)
        .join("\n");
      const contextualPrompt = contextParts
        ? `[Context]\n${contextParts}\n[/Context]\n${prompt}`
        : prompt;

      const payload = {
        session_id: currentSessionId,
        prompt: contextualPrompt,
        // Attributes the session to the trial the drawer was opened from so it
        // shows up in that trial's Ask Vivo history rather than the client's
        // portfolio history. `scope` still selects the portfolio agent — see the
        // note in AskVivoContext.
        trial_id: effectiveTrialId ?? "",
        scope: effectiveTrialId ? "trial" : "portfolio",
        filters: {
          data_sources: { includes: DATA_SOURCES, excludes: [] },
        },
      };

      const updatedHistory = [...localChatHistory];
      const last = updatedHistory[updatedHistory.length - 1];

      async function fetchResponse() {
        try {
          const data = (await chatService.sendMessage(payload)) as {
            interaction_id: string;
            output: string;
            selected_sources: string;
            timestamp: string;
            requested_clarification: boolean;
          };
          setIsChatLoading(false);
          last.interaction_id = data.interaction_id;
          last.session_id = currentSessionId;
          last.output = data.output;
          last.selected_sources = data.selected_sources;
          last.timestamp = data.timestamp;
          last.requested_clarification = data.requested_clarification;
          last.isLoading = false;
          last.hasError = false;
          setChatHistory([...updatedHistory]);
        } catch (error) {
          setIsChatLoading(false);
          console.error(error);
          last.output =
            "There was an error fetching an answer to your question. Please try again.";
          last.isLoading = false;
          last.hasError = true;
          setChatHistory([...updatedHistory]);
        }
      }
      fetchResponse();
    },
    [
      chatHistory,
      sessionId,
      effectiveTrialId,
      screenContext.contextPrefix,
      planContext,
    ],
  );

  if (isOnHiddenPage) return null;

  return (
    <>
      <Tooltip title="Ask Vivo" placement="left">
        <button
          onClick={() => setOpen(true)}
          className="fixed z-[1000] flex items-center justify-center rounded-full shadow-lg cursor-pointer transition-transform hover:scale-110 active:scale-95 border-none"
          style={{
            bottom: 48,
            right: 24,
            width: 56,
            height: 56,
            backgroundColor: "#06709B",
          }}
          aria-label="Open Ask Vivo chat"
        >
          <VivoIcon style={{ width: 24, height: 24, color: "#fff" }} />
        </button>
      </Tooltip>

      <Drawer
        title={null}
        placement="right"
        width={460}
        onClose={() => setOpen(false)}
        open={open}
        closable={false}
        styles={{
          body: {
            padding: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          },
        }}
        rootStyle={{ zIndex: 1050 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center justify-center rounded-full"
              style={{ width: 32, height: 32, backgroundColor: "#06709B" }}
            >
              <VivoIcon style={{ width: 16, height: 16 }} />
            </div>
            <span className="text-lg font-semibold text-[#2B4752]">
              Ask Vivo
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Tooltip title="New chat">
              <Button
                type="text"
                icon={<PlusOutlined />}
                onClick={handleNewChat}
                size="small"
              />
            </Tooltip>
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setOpen(false)}
              size="small"
            />
          </div>
        </div>

        {/* Messages */}
        <div
          className="flex-1 overflow-y-auto px-5 hide-scrollbar"
          style={{ minHeight: 0 }}
        >
          {chatHistory.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div
                className="flex items-center justify-center rounded-full mb-4"
                style={{ width: 48, height: 48, backgroundColor: "#06709B" }}
              >
                <VivoIcon style={{ width: 24, height: 24 }} />
              </div>
              <p className="text-[#2B4752] text-base font-medium mb-1">
                How can I help you?
              </p>
              <p className="text-[#556C75] text-sm">
                Ask me anything about your clinical trial data.
              </p>
            </div>
          ) : (
            <>
              {chatHistory.map((chat) => (
                <DrawerChatItem
                  key={chat.entryId}
                  chat={chat}
                  userFirstInitial={userFirstInitial}
                />
              ))}
              <div ref={bottomRef} />
            </>
          )}
        </div>

        {/* Input */}
        <div className="px-5 py-4 border-t border-gray-200">
          {screenContext.screenName && (
            <div className="flex items-center gap-1.5 mb-2 px-1">
              <span
                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: "#06709B" }}
              />
              <span className="text-xs text-[#556C75]">
                Viewing:{" "}
                <span className="font-medium text-[#2B4752]">
                  {screenContext.screenName}
                </span>
              </span>
            </div>
          )}
          <ChatInput
            isNewSession={isNewSession}
            handleSearch={handleSearch}
            isLoading={isChatLoading}
          />
          <p className="text-[#556C75] text-xs text-center mt-2">
            Verify reasoning with provided explanation.
          </p>
        </div>
      </Drawer>
    </>
  );
}
